import React, { useState } from "react";
import { Box, Fab, TableCell } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { CTable, CDialog, ErrorBox, HFlex, Loading, ProgressButton, Text } from "shared";
import { useHcp } from "api/hooks/useHCPs";
import { useHcpServices, useCreateDefaultBooking } from "api/hooks/useHcpServices";
import { ServiceItem } from "routes/hcp/types";
import { useErrorContext } from "shared";
import CreateServiceForm from "./CreateServiceForm";

const Cell: React.VFC<{ children?: React.ReactNode }> = ({ children }) => (
  <TableCell>
    <Text tiny>{children}</Text>
  </TableCell>
);

interface ServiceRow {
  id: number;
  name: string;
  mode: string;
  purpose: string;
  duration: string;
  categoryName: string;
  capacity: string;
  bookable: string;
}

const toRows = (services: ServiceItem[]): ServiceRow[] =>
  services.map((s) => ({
    id: s.id,
    name: s.name,
    mode: s.mode,
    purpose: s.purpose,
    duration: `${s.duration} min`,
    categoryName: s.category?.name ?? "—",
    capacity: s.capacity != null ? String(s.capacity) : "—",
    bookable: s.bookable_by_patients ? "Yes" : "No",
  }));

const Services: React.VFC = () => {
  const { data: hcp } = useHcp();
  const hcpId = hcp?.id ?? 0;
  const { data: services, isLoading, error } = useHcpServices(hcpId);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const setError = useErrorContext();

  const createDefaultBooking = useCreateDefaultBooking();

  const handleCreateDefault = async () => {
    try {
      await createDefaultBooking.mutateAsync(hcpId);
    } catch (err) {
      setError("Could not create default video service", err);
    }
  };

  if (!hcp) return null;

  const tableHeader = {
    name: "Name",
    mode: "Mode",
    purpose: "Purpose",
    duration: "Duration",
    categoryName: "Category",
    capacity: "Capacity",
    bookable: "Bookable by Patients",
  };

  const cellFactory = (item: ServiceRow) => ({
    name: <Cell>{item.name}</Cell>,
    mode: <Cell>{item.mode}</Cell>,
    purpose: <Cell>{item.purpose}</Cell>,
    duration: <Cell>{item.duration}</Cell>,
    categoryName: <Cell>{item.categoryName}</Cell>,
    capacity: <Cell>{item.capacity}</Cell>,
    bookable: <Cell>{item.bookable}</Cell>,
  });

  const rows = Array.isArray(services) ? toRows(services) : undefined;

  const showDefaultButton =
    Array.isArray(services) && services.length === 0 && hcp.health_care_organization_id != null;

  return (
    <Box mt={3}>
      <HFlex justifyContent="space-between" alignItems="center" mb={2}>
        <Text bold>Services</Text>
        <Box display="inline-flex" alignItems="center">
          <Text caption secondary mr={1}>
            Add Service
          </Text>
          <Fab color="secondary" size="small" onClick={() => setDialogOpen(true)} aria-label="Add Service">
            <Add />
          </Fab>
        </Box>
      </HFlex>

      {error && <ErrorBox error={error} verbose dark />}
      {isLoading && <Loading />}

      {showDefaultButton && (
        <Box mb={2}>
          <ProgressButton loading={createDefaultBooking.isLoading} onClick={handleCreateDefault}>
            Create Default Video Service
          </ProgressButton>
        </Box>
      )}

      {rows && rows.length > 0 && <CTable header={tableHeader} items={rows} cellFactory={cellFactory} />}

      <CDialog maxWidth="md" fullWidth open={isDialogOpen} onClose={() => setDialogOpen(false)}>
        <CreateServiceForm
          hcpId={hcpId}
          onSuccess={() => setDialogOpen(false)}
          onCancel={() => setDialogOpen(false)}
        />
      </CDialog>
    </Box>
  );
};

export default Services;